import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  Button,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  copyFileVersionComments,
  fetchFileComments,
  fetchFileVersionComments,
  updateFile,
} from '../../actions/Files';
import { updateShareFile } from '../../actions/Shares';

import HelpIcon from '../HelpIcon';

import { addFlashMessage } from '../../helpers/FlashMessages';

import ErrorBoundary from '../ErrorBoundary';
import ProcessingSpinner from '../ProcessingSpinner';

const CommentsCopyModal = ({
  mode,
  handleClose,
  handleCopyComments,
  files,
  file,
  fileVersion,
  currentVersionId,
  commentsCount,
  commentsCopied,
  handleCopyFileVersionComments,
  loadComments,
  updateCommentsCount,
}) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [copyValue, setCopyValue] = useState('all');
  const [copyComments, setCopyComments] = useState(true);
  const [copyRegular, setCopyRegular] = useState(true);
  const [copyAnnotations, setCopyAnnotations] = useState(true);

  const handleDefaultChecks = useCallback(() => {
    if (!commentsCount) return;
    if (commentsCount.regular === 0 || commentsCopied === 'regular') setCopyRegular(false);
    if (commentsCount.annotations === 0 || commentsCopied === 'annotations') setCopyAnnotations(false);
  }, [commentsCopied, commentsCount]);

  const handleChangeValue = (type, value) => {
    if (type === 'comments') {
      setCopyComments(value);
      if (!value) {
        setCopyValue(null);
        setCopyRegular(false);
        setCopyAnnotations(false);
      } else {
        setCopyRegular(true);
        setCopyAnnotations(true);
      }
    }
    if (type === 'regular') {
      setCopyRegular(value);
    }
    if (type === 'annotations') {
      setCopyAnnotations(value);
    }
    handleDefaultChecks();
  };

  const handleConfirm = () => {
    if (mode === 'copy-comments') {
      setLoading(true);
      handleCopyFileVersionComments(file.uuid, fileVersion, { copy_comments: copyValue })
        .then(() => {
          setLoading(false);
          if (!currentVersionId) {
            loadComments(file.uuid, currentVersionId, true).then((commentsResponse) => {
              updateCommentsCount(commentsResponse.comments_count, currentVersionId);
              addFlashMessage({
                type: 'success',
                text: intl.formatMessage({ defaultMessage: 'Comments have been copied' }),
              });
            });
          }

          handleClose();
        });
    } else if (mode === 'create-version') {
      handleCopyComments(files, copyValue);
      if (!currentVersionId) {
        loadComments(file.uuid, currentVersionId, true).then((commentsResponse) => {
          updateCommentsCount(commentsResponse.comments_count, currentVersionId);
        });
      }
      handleClose();
    }
  };

  useEffect(() => {
    handleDefaultChecks();
  }, [handleDefaultChecks]);

  useEffect(() => {
    if (copyRegular && copyAnnotations) {
      setCopyValue('all');
    } else if (copyRegular && !copyAnnotations) {
      setCopyValue('regular');
    } else if (!copyRegular && copyAnnotations) {
      setCopyValue('annotations');
    } else {
      setCopyValue(null);
    }
  }, [copyAnnotations, copyRegular]);

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        {intl.formatMessage({ defaultMessage: 'Copy comments' })}
      </ModalHeader>
      <ModalBody>
        <ListGroup flush className="switch-list copy-comments-list">
          <ListGroupItem>
            <Label for="switchGroupCopyComments" className="switch">
              <div className="switch-details">
                <span>
                  {intl.formatMessage({
                    defaultMessage: 'Copy {count} {count, plural, one {comment} other {comments}} to the new version?',
                  }, { count: commentsCount ? commentsCount.all : 0 })}
                </span>
              </div>
              <div className={classnames('switch-control input-inner')}>
                <Input
                  type="checkbox"
                  name="groupUploadFile"
                  id="switchGroupCopyComments"
                  checked={copyComments}
                  onChange={e => handleChangeValue('comments', e.target.checked)}
                />
                <i />
              </div>
            </Label>
          </ListGroupItem>
          {copyComments && (
            <>
              <ListGroupItem className={classnames('checkbox-label', { disabled: commentsCount.regular === 0 || commentsCopied === 'regular' })}>
                <div className="checkbox-label">
                  <Label for="checkboxCopyRegular" className="checkbox-label">
                    {intl.formatMessage({
                      defaultMessage: 'Copy {count} {count, plural, one {regular comment} other {regular comments}}?',
                    }, { count: commentsCount ? commentsCount.regular : 0 })}
                    {commentsCopied === 'regular' && (
                      <HelpIcon id="comments-copied-regular-tooltip" permanent={true}>
                        {intl.formatMessage({ defaultMessage: 'Regular comments were already copied from this version' })}
                      </HelpIcon>
                    )}
                  </Label>
                </div>
                <div className="checkbox-wrapper">
                  <Input
                    type="checkbox"
                    className="input-check check-l"
                    id="checkboxCopyRegular"
                    checked={copyRegular}
                    onChange={e => handleChangeValue('regular', e.target.checked)}
                    disabled={commentsCount.regular === 0}
                  />
                </div>
              </ListGroupItem>

              <ListGroupItem className={classnames({ disabled: commentsCount.annotations === 0 || commentsCopied === 'annotations' })}>
                <div className="checkbox-label">
                  <Label for="checkboxCopyAnnotations">
                    {intl.formatMessage({
                      defaultMessage: 'Copy {count} {count, plural, one {annotation} other {annotations}}?',
                    }, { count: commentsCount ? commentsCount.annotations : 0 })}
                    {commentsCopied === 'annotations' && (
                      <HelpIcon id="comments-copied-annotations-tooltip" permanent={true}>
                        {intl.formatMessage({ defaultMessage: 'Annotations were already copied from this version' })}
                      </HelpIcon>
                    )}
                  </Label>
                </div>
                <div className="checkbox-wrapper">
                  <Input
                    type="checkbox"
                    className="input-check check-l"
                    id="checkboxCopyAnnotations"
                    checked={copyAnnotations}
                    onChange={e => handleChangeValue('annotations', e.target.checked)}
                    disabled={commentsCount.annotations === 0}
                  />
                </div>
              </ListGroupItem>
            </>
          )}
        </ListGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose}>
          {intl.formatMessage({ defaultMessage: 'Close' })}
        </Button>
        <Button color="primary" onClick={() => handleConfirm()} className={classnames({ disabled: loading })}>
          <ProcessingSpinner show={loading} />
          {intl.formatMessage({ defaultMessage: 'Confirm' })}
        </Button>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapDispatchToProps = dispatch => ({
  updateFileAction: file => (
    dispatch(updateFile(file))
  ),
  updateShareFileAction: (shareUuid, file) => (
    dispatch(updateShareFile(shareUuid, file.uuid, { file }))
  ),
  handleCopyFileVersionComments: (fileUuid, fileVersion, data) => (
    dispatch(copyFileVersionComments(fileUuid, fileVersion, data))
  ),
  loadComments: (fileUuid, fileVersion = null, silent = false) => {
    if (fileVersion) {
      return dispatch(fetchFileVersionComments(fileUuid, fileVersion, silent));
    }
    return dispatch(fetchFileComments(fileUuid, silent));
  },
});

const mapStateToProps = state => ({
  share: state.Shares.share,
});

CommentsCopyModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleCopyComments: PropTypes.func,
  handleCopyFileVersionComments: PropTypes.func,
  mode: PropTypes.string.isRequired,
  files: PropTypes.object,
  file: PropTypes.object,
  fileVersion: PropTypes.number,
  currentVersionId: PropTypes.number,
  commentsCount: PropTypes.object,
  commentsCopied: PropTypes.string,
  loadComments: PropTypes.func.isRequired,
  updateCommentsCount: PropTypes.func.isRequired,
};

CommentsCopyModal.defaultProps = {
  handleCopyComments: () => {},
  handleCopyFileVersionComments: () => {},
  files: null,
  file: null,
  fileVersion: null,
  currentVersionId: null,
  commentsCount: null,
  commentsCopied: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(CommentsCopyModal);
